import { useEffect, useRef, useCallback, useState } from "react";
import { useNetworkState } from "./useNetworkState";

interface RetryConfig {
    maxRetries: number;
    retryDelay: number;
    backoffMultiplier: number;
}

const DEFAULT_CONFIG: RetryConfig = {
    maxRetries: 3,
    retryDelay: 1000, // 1 second
    backoffMultiplier: 2,
};

/**
 * Hook to provide network-aware retry logic for data fetching operations
 *
 * This hook wraps refetch functions with intelligent retry logic that:
 * - Automatically retries failed requests during network transitions
 * - Uses exponential backoff for retry timing
 * - Respects network state to avoid unnecessary retries
 */
export const useNetworkAwareRetry = (
    refetchFunctions: (() => void | Promise<any>)[],
    config: Partial<RetryConfig> = {},
) => {
    const networkState = useNetworkState();
    const retryConfig = { ...DEFAULT_CONFIG, ...config };

    const retryCountRef = useRef<number>(0);
    const retryTimeoutRef = useRef<NodeJS.Timeout>();
    const lastRetryAttemptRef = useRef<number>(0);
    const [isRefetching, setIsRefetching] = useState<boolean>(false);

    // Clear timeout on unmount
    useEffect(() => {
        return () => {
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
            }
        };
    }, []);

    // Function to execute all refetch functions and track completion
    const executeRefetches = useCallback(async () => {
        if (isRefetching) return; // Prevent concurrent executions

        setIsRefetching(true);

        try {
            // Execute all refetch functions and wait for any promises
            const refetchPromises = refetchFunctions.map(async (refetch) => {
                if (typeof refetch === "function") {
                    const result = refetch();
                    // If refetch returns a promise, wait for it
                    if (result && typeof result.then === "function") {
                        await result;
                    }
                }
            });

            // Wait for all refetches to complete
            await Promise.allSettled(refetchPromises);

            // Add a small buffer to ensure UI has updated
            setTimeout(() => {
                setIsRefetching(false);
            }, 500);

        } catch (error) {
            console.warn("[useNetworkAwareRetry] Error executing refetch:", error);
            setIsRefetching(false);
        }
    }, [refetchFunctions, isRefetching]);

    // Retry with exponential backoff
    const retryWithBackoff = useCallback(() => {
        if (retryCountRef.current >= retryConfig.maxRetries || !networkState.isOnline) {
            return;
        }

        const delay = retryConfig.retryDelay * retryConfig.backoffMultiplier ** retryCountRef.current;
        retryCountRef.current += 1;
        lastRetryAttemptRef.current = Date.now();

        retryTimeoutRef.current = setTimeout(() => {
            executeRefetches();
        }, delay);
    }, [retryConfig, networkState.isOnline, executeRefetches]);

    // Reset retry count when network comes back online
    useEffect(() => {
        if (networkState.isOnline && networkState.isReconnecting) {
            retryCountRef.current = 0;

            // Clear any existing retry timeout
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
            }

            // Trigger an immediate retry when network comes back
            setTimeout(() => {
                executeRefetches();
            }, 500); // Small delay to allow network to stabilize
        }
    }, [networkState.isOnline, networkState.isReconnecting, executeRefetches]);

    // Manual retry function
    const manualRetry = useCallback(() => {
        retryCountRef.current = 0;

        if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
        }

        executeRefetches();
    }, [executeRefetches]);

    return {
        retry: manualRetry,
        retryWithBackoff,
        canRetry: retryCountRef.current < retryConfig.maxRetries && networkState.isOnline,
        retryCount: retryCountRef.current,
        isRetrying: !!retryTimeoutRef.current,
        isRefetching,
        networkState,
    };
};